import { MAX_NAME_LENGTH } from '~/consts';
import events from '~/modules/game-events/game-events';
import { defineMutation, defineQuery } from '~/modules/network/rpc/define';
import { RpcContext } from '~/modules/network/rpc/types';
import { PlayerNumber } from '~/modules/players/player-number';
import { keyStrokes } from '~/modules/remote-mic/network/messages';
import RemoteMicManager from '~/modules/remote-mic/remote-mic-manager';
import SongQueue from '~/modules/song-queue/song-queue';
import SongDao from '~/modules/songs/songs-service';
import { InputLagSetting, UnassignOnSongFinishedSetting } from '~/routes/settings/settings-state';

// All server-side RPC handlers, organized by namespace.
// Permission key:
//   defineQuery  → defaults to 'read'  (any connected client)
//   defineMutation → defaults to 'write' (only clients with write permission)
// Override with { permission: 'read' | 'write' } to change the default.
export const serverHandlers = {
  songs: {
    getSongList: defineQuery(async (_ctx: RpcContext) => {
      const [custom, deleted] = await Promise.all([SongDao.getLocalIndex(), SongDao.getDeletedSongsList()]);
      return {
        custom: custom.map((song) => ({
          artist: song.artist,
          title: song.title,
          video: song.video,
          language: song.language,
          search: song.search,
          // The phone's Duets and Length filters read these, and a locally added song that answered
          // neither would quietly count as a solo song of unknown length on the phone's own list.
          tracksCount: song.tracksCount,
          duration: song.duration,
        })),
        deleted,
      };
    }),

    search: defineMutation((_ctx: RpcContext, search: string) => {
      events.remoteSongSearch.dispatch(search);
    }),

    select: defineMutation((_ctx: RpcContext, id: string) => {
      events.remoteSongSelected.dispatch(id);
    }),

    // Adds to the party's running order rather than interrupting whatever is on now. A mutation,
    // so it takes the same write permission as selecting: a read-only phone is a microphone.
    queue: defineMutation((_ctx: RpcContext, id: string) => {
      events.remoteSongQueued.dispatch(_ctx.senderId, id);
    }),

    // Switch the song selection screen's playlist from the phone's mirrored toolbar.
    setPlaylist: defineMutation((_ctx: RpcContext, name: string) => {
      events.remotePlaylistSelected.dispatch(name);
    }),

    // Jump the song list to a group from the phone's mirrored group nav row.
    scrollToGroup: defineMutation((_ctx: RpcContext, name: string) => {
      events.remoteSongGroupSelected.dispatch(name);
    }),

    // Any connected mic can update its own favourites list
    sendMyList: defineMutation(
      (_ctx: RpcContext, delta: { added?: string[]; deleted?: string[] }) => {
        events.remoteMicSongListUpdated.dispatch(_ctx.senderId, delta);
      },
      { permission: 'read' },
    ),
  },

  // Any phone that can drive the game may reorder or drop any queued song, not only its own.
  queue: {
    remove: defineMutation((_ctx: RpcContext, id: string) => {
      SongQueue.remove(id);
    }),

    moveUp: defineMutation((_ctx: RpcContext, id: string) => {
      SongQueue.move(id, -1);
    }),
  },

  settings: {
    // Game-level input lag (requires write — controls global game setting)
    getInputLag: defineQuery((_ctx: RpcContext) => InputLagSetting.get(), { permission: 'write' }),

    setInputLag: defineMutation((_ctx: RpcContext, value: number) => {
      InputLagSetting.set(value);
      return InputLagSetting.get();
    }),

    // Per-microphone input lag (read permission — each mic controls its own lag)
    getMicrophoneLag: defineQuery((ctx: RpcContext) => {
      const microphone = RemoteMicManager.getRemoteMicById(ctx.senderId);
      if (!microphone) throw new Error('Microphone not found');
      return microphone.getInput().getInputLag();
    }),

    setMicrophoneLag: defineMutation(
      (ctx: RpcContext, value: number) => {
        const microphone = RemoteMicManager.getRemoteMicById(ctx.senderId);
        if (!microphone) throw new Error('Microphone not found');
        microphone.getInput().setInputLag(value);
        return microphone.getInput().getInputLag();
      },
      { permission: 'read' },
    ),

    getUnassignAfterSong: defineQuery((_ctx: RpcContext) => UnassignOnSongFinishedSetting.get(), {
      permission: 'write',
    }),

    setUnassignAfterSong: defineMutation((_ctx: RpcContext, state: boolean) => {
      UnassignOnSongFinishedSetting.set(state);
      return UnassignOnSongFinishedSetting.get();
    }),
  },

  input: {
    keystroke: defineMutation((_ctx: RpcContext, key: keyStrokes) => {
      events.remoteKeyboardPressed.dispatch(key);
    }),

    // Directly activate a mirrored control by its register() name (mirror mode). Same write
    // permission as keystroke, since it drives the same on-screen actions.
    activateControl: defineMutation((_ctx: RpcContext, name: string) => {
      events.remoteControlActivated.dispatch(name);
    }),

    // Push a new value for a mirrored value control (e.g. a text field) edited on the remote. Same
    // write permission as activateControl, since it drives the same on-screen state.
    setControlValue: defineMutation((_ctx: RpcContext, name: string, value: string) => {
      events.remoteControlValueChanged.dispatch(name, value);
    }),

    // Any connected mic can confirm its readiness (no write permission required)
    confirmReadiness: defineMutation(
      (ctx: RpcContext) => {
        events.readinessConfirmed.dispatch(ctx.senderId);
      },
      { permission: 'read' },
    ),
  },

  profiles: {
    // The photograph a phone took for the profile form open on the television. A write, like every
    // other thing a phone does that changes something which is not about that phone.
    setPhoto: defineMutation((_ctx: RpcContext, dataUrl: string) => {
      if (typeof dataUrl !== 'string' || !dataUrl.startsWith('data:image/')) throw new Error('Not an image');
      events.remoteProfilePhotoTaken.dispatch(dataUrl);
    }),
  },

  players: {
    requestMicSelect: defineMutation((_ctx: RpcContext, id: string, playerNumber: PlayerNumber | null) => {
      // Throws when the seat is refused (see `event-listeners.tsx`), which rejects the call.
      events.playerChangeRequested.dispatch(id, playerNumber);
    }),

    removePlayer: defineMutation((ctx: RpcContext, id: string) => {
      // Players can remove other players (this is intentional)
      //
      // Freeing the slot is part of the eviction, not a side effect of the disconnect: a phone that
      // merely drops out keeps its slot on purpose, so a reconnect lands back on it (see
      // `slot-state.ts`). Being removed by somebody else is the opposite intent - the slot should be
      // free for whoever picks the phone up next.
      // Refused mid-song: the seat then stays held, like a dropped phone's, until the song ends.
      try {
        events.playerChangeRequested.dispatch(id, null);
      } catch (e) {
        console.warn('Seat kept while the song plays', e);
      }
      ctx.removePlayer(id);
      // Closed at the relay, so the game has to let go of it too or it lingers until pings time out.
      RemoteMicManager.removeRemoteMic(id);
    }),

    // Any connected mic can rename itself (no write permission required)
    setName: defineMutation(
      (ctx: RpcContext, name: string) => {
        const trimmedName = typeof name === 'string' ? name.trim().slice(0, MAX_NAME_LENGTH) : '';
        if (!trimmedName) throw new Error('Invalid name');

        const remoteMic = RemoteMicManager.getRemoteMicById(ctx.senderId);
        if (!remoteMic) throw new Error('Microphone not found');
        // Same rule as joining: one singer is one phone, so renaming onto a name somebody else is
        // singing under lands on a free variation rather than creating a second claim on it.
        const claimed = RemoteMicManager.freeSingerName(trimmedName, ctx.senderId);
        remoteMic.name = claimed;
        events.remoteMicRenamed.dispatch({ id: ctx.senderId, name: claimed });
      },
      { permission: 'read' },
    ),
  },
} as const;

export type ServerHandlers = typeof serverHandlers;
